import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { MEALS, MEAL_SPLIT } from './trackingConstants';

const COLORS = { calories: '#E91E63', carbs: '#F59E0B', protein: '#3B82F6', fat: '#10B981', fiber: '#A78BFA' };

const NutrientRow = ({ label, current, goal, unit, color }) => {
  const pct = goal ? Math.min(100, (current / goal) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm font-semibold text-zinc-300">{label}</span>
        <span className="text-sm font-bold text-white tabular-nums">{Math.round(current)} / {Math.round(goal)} {unit}</span>
      </div>
      <div className="h-2.5 rounded-full bg-white/8 overflow-hidden">
        <motion.div className="h-full rounded-full" style={{ background: color }} initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8, ease: 'easeOut' }} />
      </div>
    </div>
  );
};

const mealTotals = (entries, mealKey) => (entries || [])
  .filter((e) => e.meal === mealKey)
  .reduce((acc, e) => ({
    calories: acc.calories + (e.calories || 0), protein: acc.protein + (e.protein || 0),
    carbs: acc.carbs + (e.carbs || 0), fat: acc.fat + (e.fat || 0), fiber: acc.fiber + (e.fiber || 0),
  }), { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 });

const mealBudget = (goals) => (mealKey) => {
  const ratio = MEAL_SPLIT[mealKey] || 0.2;
  return {
    calories: goals.calories * ratio, protein: goals.protein * ratio,
    carbs: goals.carbs * ratio, fat: goals.fat * ratio,
  };
};

/** Page "Détails" (façon Yazio) : apport nutritionnel complet + répartition par repas. */
export const DayDetailView = ({ summary, entries, en, onBack, dateLabel }) => {
  const [activeMeal, setActiveMeal] = useState(MEALS[0].key);
  const { totals, goals } = summary;
  const budgetOf = mealBudget(goals);
  const mt = mealTotals(entries, activeMeal);
  const mb = budgetOf(activeMeal);

  return (
    <div data-testid="day-detail-view">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="p-2 rounded-xl border border-white/10 text-zinc-300 hover:border-primary/40 hover:text-white transition-colors" data-testid="day-detail-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h2 className="text-lg font-bold text-white capitalize">{dateLabel}</h2>
      </div>

      <div className="rounded-3xl bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.25)] p-5 mb-6 space-y-5" data-testid="nutrient-breakdown-card">
        <h3 className="font-bold text-white text-sm uppercase tracking-wide text-zinc-400">{en ? 'Nutritional intake' : 'Apport nutritionnel'}</h3>
        <NutrientRow label={en ? 'Calories' : 'Calories'} current={totals.calories} goal={goals.calories} unit="kcal" color={COLORS.calories} />
        <NutrientRow label={en ? 'Carbs' : 'Glucides'} current={totals.carbs} goal={goals.carbs} unit="g" color={COLORS.carbs} />
        <NutrientRow label={en ? 'Protein' : 'Protéines'} current={totals.protein} goal={goals.protein} unit="g" color={COLORS.protein} />
        <NutrientRow label={en ? 'Fat' : 'Lipides'} current={totals.fat} goal={goals.fat} unit="g" color={COLORS.fat} />
        {totals.fiber > 0 && <NutrientRow label={en ? 'Fiber' : 'Fibres'} current={totals.fiber} goal={Math.max(totals.fiber, 25)} unit="g" color={COLORS.fiber} />}
      </div>

      <div className="rounded-3xl bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.25)] p-5" data-testid="meal-breakdown-card">
        <h3 className="font-bold text-white text-sm uppercase tracking-wide text-zinc-400 mb-4">{en ? 'Breakdown by meal' : 'Répartition par repas'}</h3>
        <div className="flex gap-2 overflow-x-auto pb-1 mb-5 -mx-1 px-1">
          {MEALS.map((m) => (
            <button
              key={m.key} onClick={() => setActiveMeal(m.key)}
              className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border text-sm font-semibold transition-all ${activeMeal === m.key ? 'bg-primary/15 border-primary text-white' : 'bg-zinc-800/60 border-white/5 text-zinc-400 hover:border-white/20'}`}
              data-testid={`meal-pill-${m.key}`}
            >
              <m.icon className={`w-4 h-4 ${activeMeal === m.key ? 'text-primary' : ''}`} /> {en ? m.en : m.fr}
            </button>
          ))}
        </div>
        <div className="space-y-4">
          <NutrientRow label="Calories" current={mt.calories} goal={mb.calories} unit="kcal" color={COLORS.calories} />
          <NutrientRow label={en ? 'Carbs' : 'Glucides'} current={mt.carbs} goal={mb.carbs} unit="g" color={COLORS.carbs} />
          <NutrientRow label={en ? 'Protein' : 'Protéines'} current={mt.protein} goal={mb.protein} unit="g" color={COLORS.protein} />
          <NutrientRow label={en ? 'Fat' : 'Lipides'} current={mt.fat} goal={mb.fat} unit="g" color={COLORS.fat} />
        </div>
      </div>
    </div>
  );
};

export default DayDetailView;
